import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import api, { changePassword, setup2FA, verify2FA, disable2FA } from '../lib/api';
import AddressSearchInput from '../components/AddressSearchInput';
import { 
  Bell, Mail, MessageSquare, Smartphone, Shield, Key, 
  ChevronRight, Check, X, Loader2, Eye, EyeOff,
  AlertCircle, Lock, User, Crown, Sparkles, Users, Calendar, Brain,
  Home, MapPin, Zap, Infinity, Gift
} from 'lucide-react';
import { toast } from 'sonner';

// Official Brand Icons
const AppleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const MetaIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path fill="#0082FB" d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.52 1.49-3.92 3.78-3.92 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
  </svg>
);

// Floating Widget
const FloatingWidget = ({ children, className = '' }) => (
  <div
    className={`relative ${className}`}
    style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(40px)',
      borderRadius: '20px',
      boxShadow: '0 2px 16px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03)',
    }}
  >
    {children}
  </div>
);

// Setting Row Component
const SettingRow = ({ icon: Icon, iconBg, label, description, children, comingSoon }) => (
  <div className={`flex items-center justify-between py-4 ${comingSoon ? 'opacity-60' : ''}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg || 'bg-gray-100'}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-800 text-sm">{label}</p>
          {comingSoon && (
            <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-medium">
              Coming Soon
            </span>
          )}
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
    </div>
    <div>{children}</div>
  </div>
);

// Social Login Button
const SocialButton = ({ icon: Icon, label, connected, comingSoon, onClick }) => (
  <button
    onClick={onClick}
    disabled={comingSoon}
    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
      comingSoon 
        ? 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-60' 
        : connected 
          ? 'bg-green-50 border-green-200' 
          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
    }`}
  >
    <div className="flex items-center gap-3">
      <Icon className="h-6 w-6" />
      <span className="font-medium text-sm">{label}</span>
      {comingSoon && (
        <span className="text-[10px] px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full font-medium">
          Coming Soon
        </span>
      )}
    </div>
    {connected ? (
      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
        <Check className="h-3 w-3" /> Connected
      </span>
    ) : !comingSoon ? (
      <ChevronRight className="h-4 w-4 text-gray-400" />
    ) : null}
  </button>
);

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Account status
  const [accountStatus, setAccountStatus] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [upgradingAccount, setUpgradingAccount] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly'); // 'monthly', 'annual', 'lifetime'
  
  // User settings (including home address)
  const [userSettings, setUserSettings] = useState(null);
  const [homeAddress, setHomeAddress] = useState('');
  const [homeAddressCoords, setHomeAddressCoords] = useState(null);
  const [savingHomeAddress, setSavingHomeAddress] = useState(false);
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    inApp: true,
    push: false,
    email: false,
    text: false
  });
  
  // Password change
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // 2FA
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAStep, setTwoFAStep] = useState(1);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  // Load account status and settings on mount
  useEffect(() => {
    loadAccountStatus();
    loadUserSettings();
  }, []);

  const loadAccountStatus = async () => {
    try {
      const response = await api.get('/account/status');
      setAccountStatus(response.data);
    } catch (error) {
      console.error('Failed to load account status:', error);
    } finally {
      setLoadingAccount(false);
    }
  };

  const loadUserSettings = async () => {
    try {
      const response = await api.get('/settings');
      const settings = response.data.settings;
      setUserSettings(settings);
      if (settings?.home_address) {
        setHomeAddress(settings.home_address.address || '');
        setHomeAddressCoords({
          lat: settings.home_address.lat,
          lon: settings.home_address.lon
        });
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  };

  const handleHomeAddressChange = (address, coords) => {
    setHomeAddress(address);
    setHomeAddressCoords(coords);
  };

  const saveHomeAddress = async () => {
    if (!homeAddress.trim()) {
      toast.error('Please enter your home address');
      return;
    }
    
    setSavingHomeAddress(true);
    try {
      await api.put('/settings', {
        home_address: {
          address: homeAddress,
          display_name: homeAddress,
          lat: homeAddressCoords?.lat || null,
          lon: homeAddressCoords?.lon || null
        }
      });
      toast.success('Home address saved! Travel times will now be calculated.');
      loadUserSettings();
    } catch (error) {
      toast.error('Failed to save home address');
    } finally {
      setSavingHomeAddress(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgradingAccount(true);
    try {
      await api.post('/account/upgrade/v2', { subscription_type: selectedPlan });
      const planNames = {
        monthly: 'Pro Monthly',
        annual: 'Pro Annual',
        lifetime: 'Pro Lifetime'
      };
      toast.success(`Account upgraded to ${planNames[selectedPlan]}!`);
      loadAccountStatus();
    } catch (error) {
      toast.error('Failed to upgrade account');
    } finally {
      setUpgradingAccount(false);
    }
  };

  const handleDowngrade = async () => {
    try {
      await api.post('/account/downgrade');
      toast.success('Account downgraded to Free');
      loadAccountStatus();
    } catch (error) {
      toast.error('Failed to downgrade account');
    }
  };

  const handleNotificationChange = (key, value) => {
    if (key === 'email' || key === 'text') {
      toast.info('This feature is coming soon!');
      return;
    }
    setNotifications(prev => ({ ...prev, [key]: value }));
    toast.success(`${key === 'inApp' ? 'In-app' : 'Push'} notifications ${value ? 'enabled' : 'disabled'}`);
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      // STRUCT-2: Real API call instead of setTimeout
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      loadAccountStatus(); // BUG-6: Refresh account status after password change
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handle2FASetup = async () => {
    if (twoFAStep === 1) {
      setTwoFALoading(true);
      try {
        // STRUCT-1: Real API call to setup 2FA
        const response = await setup2FA();
        setQrCode(response.secret);
        // Store provisioning URI for QR code display if needed
        setTwoFAStep(2);
      } catch (error) {
        const errorMsg = error.response?.data?.detail || 'Failed to generate 2FA setup';
        toast.error(errorMsg);
      } finally {
        setTwoFALoading(false);
      }
    } else if (twoFAStep === 2) {
      if (twoFACode.length !== 6) {
        toast.error('Please enter a valid 6-digit code');
        return;
      }
      setTwoFALoading(true);
      try {
        // STRUCT-1: Real API call to verify 2FA
        const response = await verify2FA(twoFACode);
        setBackupCodes(response.backup_codes);
        setTwoFAStep(3);
      } catch (error) {
        const errorMsg = error.response?.data?.detail || 'Invalid verification code';
        toast.error(errorMsg);
      } finally {
        setTwoFALoading(false);
      }
    } else if (twoFAStep === 3) {
      setTwoFAEnabled(true);
      setShow2FADialog(false);
      setTwoFAStep(1);
      setTwoFACode('');
      toast.success('Two-factor authentication enabled!');
      loadAccountStatus(); // BUG-6: Refresh account status after 2FA change
    }
  };

  const handleDisable2FA = async () => {
    // For now, just disable locally - in production would require password verification
    setTwoFAEnabled(false);
    toast.success('Two-factor authentication disabled');
    loadAccountStatus(); // BUG-6: Refresh account status after 2FA change
  };

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-sm text-gray-500">Manage your account preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Profile Section */}
          <FloatingWidget className="p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <h2 className="font-semibold text-gray-800">{user?.name || 'User'}</h2>
                <p className="text-sm text-gray-500">{user?.email || 'user@example.com'}</p>
              </div>
            </div>
          </FloatingWidget>

          {/* Home Base Address Section */}
          <FloatingWidget className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Home className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Home Base Address</h3>
                <p className="text-xs text-gray-500">Used to calculate travel times to events</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <AddressSearchInput
                value={homeAddress}
                onChange={handleHomeAddressChange}
                placeholder="Search for your home address..."
                showTravelTime={false}
                className="w-full"
              />
              
              {homeAddressCoords?.lat && (
                <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <MapPin className="h-3 w-3" />
                  <span>Location verified • Travel times will be calculated for events</span>
                </div>
              )}
              
              <Button
                onClick={saveHomeAddress}
                disabled={savingHomeAddress || !homeAddress.trim()}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500"
                data-testid="save-home-address-btn"
              >
                {savingHomeAddress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Home Address
              </Button>
            </div>
          </FloatingWidget>

          {/* Subscription Section */}
          <FloatingWidget className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Subscription</h3>
                <p className="text-xs text-gray-500">Manage your plan</p>
              </div>
              {accountStatus?.account_type === 'pro' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full text-sm font-medium">
                  <Crown className="h-4 w-4" /> Pro
                </span>
              )}
            </div>

            {loadingAccount ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : accountStatus?.account_type === 'pro' ? (
              <div className="space-y-4">
                {/* Pro Plan Active */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <Crown className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Pro Plan</p>
                    <p className="text-xs text-gray-500">$9.99/month</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-purple-600" />
                    <span>Unlimited kids, activities & events</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-purple-600" />
                    <span>AI-powered summaries enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-purple-600" />
                    <span>SmartDaddy AI assistant access</span>
                  </div>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full rounded-xl h-11 text-gray-500"
                onClick={handleDowngrade}
              >
                Downgrade to Free
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Free Plan - Show Usage */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm font-medium text-gray-700 mb-3">Current Usage</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Users className="h-4 w-4" /> Kids
                      </span>
                      <span className={accountStatus?.at_limit?.kids ? 'text-red-600 font-medium' : 'text-gray-900'}>
                        {accountStatus?.usage?.kids || 0} / {accountStatus?.limits?.kids}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${accountStatus?.at_limit?.kids ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.min((accountStatus?.usage?.kids || 0) / 2 * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Activities
                      </span>
                      <span className={accountStatus?.at_limit?.activities ? 'text-red-600 font-medium' : 'text-gray-900'}>
                        {accountStatus?.usage?.activities || 0} / {accountStatus?.limits?.activities}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${accountStatus?.at_limit?.activities ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min((accountStatus?.usage?.activities || 0) / 2 * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Events this week
                      </span>
                      <span className={accountStatus?.at_limit?.weekly_events ? 'text-red-600 font-medium' : 'text-gray-900'}>
                        {accountStatus?.usage?.weekly_events || 0} / {accountStatus?.limits?.weekly_events}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${accountStatus?.at_limit?.weekly_events ? 'bg-red-500' : 'bg-orange-500'}`}
                        style={{ width: `${Math.min((accountStatus?.usage?.weekly_events || 0) / 5 * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Upgrade Coming Soon */}
              <div className="p-6 bg-gradient-to-br from-gray-50 to-slate-100 rounded-xl border border-gray-200">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <Crown className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Pro Plans Coming Soon</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We're working on amazing Pro features including unlimited kids, AI-powered summaries, and more!
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    <Sparkles className="h-4 w-4" />
                    Stay tuned for updates
                  </div>
                </div>
              </div>
            </div>
          )}
        </FloatingWidget>

        {/* Notifications Section */}
        <FloatingWidget className="p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Notifications</h3>
          <p className="text-xs text-gray-500 mb-4">Choose how you want to be notified</p>
          
          <div className="divide-y divide-gray-100">
            <SettingRow 
              icon={Bell} 
              iconBg="bg-gradient-to-br from-blue-500 to-cyan-500"
              label="In-App Notifications"
              description="Get notified within the app"
            >
              <Switch 
                checked={notifications.inApp} 
                onCheckedChange={(v) => handleNotificationChange('inApp', v)}
              />
            </SettingRow>
            
            <SettingRow 
              icon={Smartphone} 
              iconBg="bg-gradient-to-br from-purple-500 to-pink-500"
              label="Push Notifications"
              description="Receive push notifications"
            >
              <Switch 
                checked={notifications.push} 
                onCheckedChange={(v) => handleNotificationChange('push', v)}
              />
            </SettingRow>
            
            <SettingRow 
              icon={Mail} 
              iconBg="bg-gradient-to-br from-orange-500 to-red-500"
              label="Email Notifications"
              description="Get updates via email"
              comingSoon
            >
              <Switch 
                checked={notifications.email} 
                onCheckedChange={(v) => handleNotificationChange('email', v)}
                disabled
              />
            </SettingRow>
            
            <SettingRow 
              icon={MessageSquare} 
              iconBg="bg-gradient-to-br from-green-500 to-teal-500"
              label="Text Notifications"
              description="Receive SMS updates"
              comingSoon
            >
              <Switch 
                checked={notifications.text} 
                onCheckedChange={(v) => handleNotificationChange('text', v)}
                disabled
              />
            </SettingRow>
          </div>
        </FloatingWidget>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
        {/* Security Section */}
        <FloatingWidget className="p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Security</h3>
          <p className="text-xs text-gray-500 mb-4">Keep your account secure</p>
          
          <div className="divide-y divide-gray-100">
            <SettingRow 
              icon={Key} 
              iconBg="bg-gradient-to-br from-amber-500 to-orange-500"
              label="Change Password"
              description="Update your password"
            >
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-full h-9"
                onClick={() => setShowPasswordDialog(true)}
              >
                Change
              </Button>
            </SettingRow>
            
            <SettingRow 
              icon={Shield} 
              iconBg="bg-gradient-to-br from-emerald-500 to-green-600"
              label="Two-Factor Authentication"
              description={twoFAEnabled ? "Enabled with Google Authenticator" : "Add an extra layer of security"}
            >
              {twoFAEnabled ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <Check className="h-3 w-3" /> Enabled
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full h-9 text-red-500 border-red-200 hover:bg-red-50"
                    onClick={handleDisable2FA}
                  >
                    Disable
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full h-9"
                  onClick={() => setShow2FADialog(true)}
                >
                  Enable
                </Button>
              )}
            </SettingRow>
          </div>
        </FloatingWidget>

        {/* Connected Accounts Section */}
        <FloatingWidget className="p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Connected Accounts</h3>
          <p className="text-xs text-gray-500 mb-4">Link social accounts for easier sign-in</p>
          
          <div className="space-y-3">
            <SocialButton 
              icon={AppleIcon} 
              label="Apple" 
              comingSoon
            />
            <SocialButton 
              icon={GoogleIcon} 
              label="Google" 
              comingSoon
            />
            <SocialButton 
              icon={MetaIcon} 
              label="Meta" 
              comingSoon
            />
          </div>
        </FloatingWidget>

        {/* Danger Zone */}
        <FloatingWidget className="p-5 border border-red-100">
          <h3 className="font-semibold text-red-600 mb-1">Danger Zone</h3>
          <p className="text-xs text-gray-500 mb-4">Irreversible actions</p>
          
          <Button 
            variant="outline" 
            className="w-full rounded-xl h-11 text-red-500 border-red-200 hover:bg-red-50"
            onClick={() => toast.info('Please contact support to delete your account')}
          >
            Delete Account
          </Button>
        </FloatingWidget>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.current ? "text" : "password"}
                  placeholder="Enter current password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="h-12 rounded-xl pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.new ? "text" : "password"}
                  placeholder="Enter new password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="h-12 rounded-xl pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirm ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="h-12 rounded-xl pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="rounded-full w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handlePasswordChange} disabled={passwordLoading} className="rounded-full w-full sm:w-auto">
              {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FADialog} onOpenChange={(open) => {
        if (!open) {
          setTwoFAStep(1);
          setTwoFACode('');
        }
        setShow2FADialog(open);
      }}>
        <DialogContent className="sm:max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              {twoFAStep === 3 ? 'Save Backup Codes' : 'Set Up Two-Factor Authentication'}
            </DialogTitle>
            <DialogDescription>
              {twoFAStep === 1 && 'Add an extra layer of security to your account'}
              {twoFAStep === 2 && 'Scan the QR code with Google Authenticator'}
              {twoFAStep === 3 && 'Save these codes in a safe place'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {twoFAStep === 1 && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                  <Shield className="h-10 w-10 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    You'll need the Google Authenticator app on your phone to complete setup.
                  </p>
                </div>
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl text-left">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Make sure you have access to your authenticator app before proceeding.
                  </p>
                </div>
              </div>
            )}
            
            {twoFAStep === 2 && (
              <div className="space-y-4">
                <div className="bg-gray-100 p-6 rounded-xl text-center">
                  {/* Mock QR Code */}
                  <div className="w-40 h-40 mx-auto bg-white p-2 rounded-lg shadow-inner">
                    <div className="w-full h-full grid grid-cols-8 gap-0.5">
                      {[...Array(64)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`aspect-square ${Math.random() > 0.5 ? 'bg-gray-900' : 'bg-white'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">Scan with Google Authenticator</p>
                </div>
                
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-2">Or enter this code manually:</p>
                  <code className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono">
                    {qrCode}
                  </code>
                </div>
                
                <div className="space-y-2">
                  <Label>Enter 6-digit code from app</Label>
                  <Input
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    className="h-12 rounded-xl text-center text-xl tracking-widest font-mono"
                  />
                </div>
              </div>
            )}
            
            {twoFAStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Save these backup codes. You can use them to access your account if you lose your phone.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="px-3 py-2 bg-gray-100 rounded-lg text-center">
                      <code className="text-sm font-mono">{code}</code>
                    </div>
                  ))}
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full rounded-xl"
                  onClick={() => {
                    navigator.clipboard.writeText(backupCodes.join('\n'));
                    toast.success('Backup codes copied!');
                  }}
                >
                  Copy All Codes
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShow2FADialog(false)} 
              className="rounded-full w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={handle2FASetup} 
              disabled={twoFALoading || (twoFAStep === 2 && twoFACode.length !== 6)} 
              className="rounded-full w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-green-600"
            >
              {twoFALoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {twoFAStep === 1 ? 'Continue' : twoFAStep === 2 ? 'Verify Code' : 'Done'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
